/**
 * 客户端软门闸：决策来自 catalog + AccessSession；缺权限留在 URL。
 *
 * Route/callers: authenticated layout；onboarding / invitation 页面
 */

import { EmptyState } from "@heroui-pro/react/empty-state";
import { ShieldExclamation } from "@gravity-ui/icons";
import { AppLink as Link } from "@/components/ui/app-link";
import { useAppPathname as usePathname } from "@/lib/navigation/use-app-pathname";
import { use, useEffect, type ReactNode } from "react";

import { RequestFailureScreen } from "../errors/request-failure-screen";
import {
  normalizeApplicationPathname,
  toStaticExportHref,
} from "../../config/normalize-application-pathname";
import {
  getRoutePolicyForPathname,
  resolveWorkspaceLandingPath,
} from "../../config/route-policies";
import {
  resolveRouteAccess,
  type ProtectedRoute,
} from "../../lib/auth/route-access";
import { useAppRouter } from "@/lib/navigation/use-app-router";
import { useAppI18n } from "../../i18n/context";
import { useAccessSession } from "../providers/access-session";
import type { WorkspacePermission } from "@metaadsmanager/tk-master-contracts";

/** shellPending 缺失时仍挂起，避免门闸在会话未就绪时放行子树。 */
const blockedShell: Promise<void> = new Promise(() => undefined);

function RouteAccessDenied() {
  const { t } = useAppI18n();
  return (
    <main className="status-screen">
      {/* AccessDenied — 申请权限说明 */}
      <EmptyState>
        <EmptyState.Media variant="icon">
          <ShieldExclamation width={24} height={24} />
        </EmptyState.Media>
        <EmptyState.Title>{t("routeGuard.deniedTitle")}</EmptyState.Title>
        <EmptyState.Description>
          {t("routeGuard.deniedDetail")}
        </EmptyState.Description>
        <EmptyState.Content>
          <Link
            className="account-entry-link"
            href={toStaticExportHref("/settings")}
          >
            {t("routeGuard.goToSettings")}
          </Link>
        </EmptyState.Content>
      </EmptyState>
    </main>
  );
}

export function RouteGuard({
  children,
  requiredPermission,
  requiresWorkspaceOwner,
  route,
}: {
  children: ReactNode;
  requiredPermission?: WorkspacePermission;
  requiresWorkspaceOwner?: boolean;
  route?: ProtectedRoute;
}) {
  const router = useAppRouter();
  const pathname = usePathname();
  const accessSession = useAccessSession();
  const normalizedPathname = normalizeApplicationPathname(pathname);
  const policy = route
    ? undefined
    : getRoutePolicyForPathname(normalizedPathname);
  const access = route
    ? { route, requiredPermission, requiresWorkspaceOwner }
    : policy?.access;

  const landing =
    accessSession.routeMembership === undefined
      ? "/"
      : resolveWorkspaceLandingPath(accessSession.routeMembership);

  const decision = !access
    ? accessSession.phase === "ready"
      ? ({ action: "deny" } as const)
      : ({ action: "wait" } as const)
    : resolveRouteAccess({
        authenticated: accessSession.phase !== "anonymous",
        membership: accessSession.routeMembership,
        platformRole: accessSession.platformRole,
        requiredPermission: access.requiredPermission,
        requiresWorkspaceOwner: access.requiresWorkspaceOwner,
        route: access.route,
        workspaceHomeHref: landing,
      });

  const isWorkspaceHome =
    normalizedPathname === "/" || normalizedPathname === "/dashboard";

  const redirectTo =
    decision.action === "redirect"
      ? decision.to
      : decision.action === "deny" && isWorkspaceHome && landing !== "/"
        ? landing
        : null;

  useEffect(() => {
    if (accessSession.phase === "loading" || accessSession.phase === "error") {
      return;
    }
    if (!redirectTo) return;
    if (normalizeApplicationPathname(redirectTo) === normalizedPathname) {
      return;
    }
    router.replace(toStaticExportHref(redirectTo));
  }, [
    accessSession.phase,
    normalizedPathname,
    redirectTo,
    router,
  ]);

  if (accessSession.phase === "anonymous") {
    return null;
  }

  if (accessSession.phase === "error") {
    return (
      <RequestFailureScreen
        error={accessSession.error}
        onRetry={() => accessSession.refetch()}
      />
    );
  }

  // 挂起而不是自己画 Spinner，让壳层那一次 fallback 盖住鉴权和懒加载。
  if (accessSession.phase === "loading" || decision.action === "wait") {
    use(accessSession.shellPending ?? blockedShell);
  }

  if (redirectTo && normalizeApplicationPathname(redirectTo) !== normalizedPathname) {
    return null;
  }

  if (decision.action === "deny") {
    return <RouteAccessDenied />;
  }

  return children;
}

/** authenticated layout 入口：按 pathname 查 catalog，漏行 fail-closed。 */
export function RouteGate({ children }: { children: ReactNode }) {
  return <RouteGuard>{children}</RouteGuard>;
}
